import Phaser from "phaser";
import { characterTexture } from "../assets";
import type { Piece } from "../model/board";
import { hpForTier } from "../model/balance";
import { pieceMetrics, PIECE_HOVER_HEIGHT } from "./iso";
import { COLORS } from "./palette";
import { rankPips } from "./Controls";
import { ChargeView } from "./ChargeView";

// The root follows the ground; the body can leap without dragging its shadow into the air.
export class PieceView {
  readonly root: Phaser.GameObjects.Container;
  readonly body: Phaser.GameObjects.Container;
  private readonly outline: Phaser.GameObjects.Image;
  private hovered = false;
  private mergeable = false;
  private ready = false;
  private readonly sprite: Phaser.GameObjects.Image;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly aura: Phaser.GameObjects.Ellipse;
  private readonly health: Phaser.GameObjects.Graphics;
  private readonly rank: Phaser.GameObjects.Container;
  private readonly phase: number;
  private readonly height: number;
  private readonly maxHp: number;
  private hp: number;
  private readonly healthState: { hp: number };
  private readonly idleState = { blend: 1 };
  private dormant = false;
  private locked = false;
  private selected = false;
  private chargeView?: ChargeView;

  constructor(private scene: Phaser.Scene, public piece: Piece, x: number, y: number, depth: number,
    showHealth = false, private motion = true) {
    const { size, height } = pieceMetrics(piece);
    this.height = height;
    this.maxHp = this.hp = hpForTier(piece.tier);
    this.healthState = { hp: this.hp };
    this.phase = [...piece.id].reduce((sum, char) => sum + char.charCodeAt(0), 0) * 0.43;
    this.shadow = scene.add.ellipse(0, 2, 38 + Math.min(piece.tier, 10) * 3, 10, 0x192e33, 0.27);
    this.aura = scene.add.ellipse(0, 0, 42 + Math.min(piece.tier, 10) * 4, 14,
      piece.tier >= 6 ? 0xf5d384 : COLORS[piece.element].body, 0.1);
    this.sprite = scene.add.image(0, 0, characterTexture(piece)).setOrigin(0.5, 0.9).setDisplaySize(size, size);
    this.outline = scene.add.image(0, 0, characterTexture(piece)).setOrigin(0.5, 0.9)
      .setDisplaySize(size * 1.07, size * 1.07).setTint(0xffedac).setTintMode(Phaser.TintModes.FILL).setAlpha(0.8).setVisible(false);
    this.body = scene.add.container(0, -PIECE_HOVER_HEIGHT, [this.outline, this.sprite]);
    this.rank = rankPips(scene, piece.tier, 0, 14);
    this.health = scene.add.graphics().setVisible(showHealth);
    this.root = scene.add.container(x, y, [this.shadow, this.aura, this.body, this.rank, this.health]).setDepth(depth);
    this.drawHealth();
  }

  update(time: number): void {
    this.chargeView?.update(time);
    if (this.locked || this.dormant || !this.motion) return;
    const wave = Math.sin(time / (this.piece.element === "ice" ? 580 : 440) + this.phase) * this.idleState.blend;
    this.body.y = (this.selected ? -23 : -PIECE_HOVER_HEIGHT) + wave * 3;
    this.body.scaleY = 1 + wave * (this.piece.element === "fire" ? 0.042 : 0.018);
    this.body.scaleX = 1 - wave * 0.014;
    this.aura.alpha = (this.ready ? 0.38 : 0.07) + (wave + 1) * 0.035 + (this.selected || this.hovered ? 0.2 : 0);
    this.shadow.scaleX = 1 - wave * 0.05;
    if (this.piece.element === "lightning") this.body.angle = Math.sin(time / 170 + this.phase) * 0.8 * this.idleState.blend;
  }

  select(value: boolean): void {
    if (this.selected === value) return;
    this.selected = value;
    this.refreshOutline();
    if (!this.motion || this.dormant) return;
    this.freeze();
    this.scene.tweens.add({ targets: this.body, y: value ? -27 : -PIECE_HOVER_HEIGHT,
      scaleX: value ? 1.07 : 1, scaleY: value ? 1.07 : 1, duration: 180, ease: "Back.Out",
      onComplete: () => this.release() });
  }

  hover(value: boolean): void {
    this.hovered = value;
    this.refreshOutline();
  }

  setMergeable(value: boolean): void {
    this.mergeable = value;
    this.refreshOutline();
  }

  private refreshOutline(): void {
    // White reveals a merge; yellow still means hover or selection.
    const focused = this.selected || this.hovered;
    const scale = focused ? 1.07 : 1.1;
    this.outline.setVisible(focused || this.mergeable).setTint(focused ? 0xffedac : 0xffffff)
      .setAlpha(focused ? 0.8 : 0.9).setDisplaySize(this.sprite.displayWidth * scale, this.sprite.displayHeight * scale);
  }

  setReady(value: boolean): void {
    this.ready = value;
    this.aura.setFillStyle(value ? 0xbaffc5 : this.piece.tier >= 6 ? 0xf5d384 : COLORS[this.piece.element].body).setAlpha(value ? 0.42 : 0.1);
    this.aura.setStrokeStyle(value ? 2 : 0, 0xdcffe5, 0.8);
  }

  freeze(): void {
    this.locked = true;
    this.scene.tweens.killTweensOf(this.body);
    this.body.setScale(1).setAngle(0).setPosition(0, -PIECE_HOVER_HEIGHT);
  }

  release(): void {
    if (this.dormant) return;
    this.locked = false;
    this.root.setScale(1).setAngle(0);
    this.body.setScale(1).setAngle(0).setPosition(0, this.selected ? -23 : -PIECE_HOVER_HEIGHT);
    this.shadow.setScale(1).setAlpha(0.27);
    this.rank.setVisible(true);
    this.idleState.blend = 0;
    this.scene.tweens.killTweensOf(this.idleState);
    this.scene.tweens.add({ targets: this.idleState, blend: 1, duration: 180 });
  }

  // Travel moves the footprint along the floor, while only the body follows the arc.
  lift(height: number): void {
    this.body.y = -PIECE_HOVER_HEIGHT - height;
    this.shadow.setScale(Math.max(0.35, 1 - height / 150)).setAlpha(Math.max(0.08, 0.27 - height / 400));
    this.rank.setVisible(false);
  }

  focusPoint(): { x: number; y: number } {
    return { x: this.root.x + this.body.x, y: this.root.y + this.body.y - this.height / 2 };
  }

  face(direction: number): void {
    // Existing paintings face down-left; mirror opponents until rear art is ready.
    this.sprite.setFlipX(direction > 0); this.outline.setFlipX(direction > 0);
  }

  attackPoint(target: { x: number; y: number }): { x: number; y: number } {
    const direction = target.x >= this.root.x ? 1 : -1;
    return { x: this.root.x + direction * this.height * 0.22,
      y: this.root.y + this.body.y - this.height * 0.4 };
  }

  setHealth(hp: number, immediate = false): void {
    if (hp === this.hp && !immediate) return;
    this.hp = hp;
    this.scene.tweens.killTweensOf(this.healthState);
    if (immediate || !this.motion) { this.healthState.hp = hp; this.drawHealth(); return; }
    this.scene.tweens.add({ targets: this.healthState, hp, duration: this.motion ? 240 : 0, ease: "Cubic.Out",
      onUpdate: () => this.drawHealth(), onComplete: () => this.drawHealth() });
  }

  setCharges(count: number, animate = true): void {
    if (!this.chargeView && count > 0) {
      this.chargeView = new ChargeView(this.scene, this.piece.element, this.height, this.phase, this.motion, this.body);
    }
    this.chargeView?.setCount(count, animate);
  }

  private drawHealth(): void {
    const y = -24 - this.height;
    const ratio = this.healthState.hp / this.maxHp;
    this.health.clear().fillStyle(0x263c32, 0.95).fillRoundedRect(-29, y, 58, 10, 4);
    this.health.lineStyle(1, 0xe5cea0, 0.75).strokeRoundedRect(-29, y, 58, 10, 4);
    if (ratio > 0) {
      this.health.fillStyle(this.hp / this.maxHp > 0.35 ? 0xa9d787 : 0xf2a66b)
        .fillRoundedRect(-26, y + 3, Math.max(1, 52 * ratio), 4, 2);
    }
  }

  appear(): void {
    if (!this.motion) return;
    this.freeze(); this.rank.setVisible(false);
    this.body.setScale(0.7, 0.6).setY(-45);
    this.scene.tweens.add({ targets: this.body, y: -9, scaleX: 1.27, scaleY: 0.77, duration: 160, ease: "Cubic.In",
      onComplete: () => this.scene.tweens.add({ targets: this.body, y: -PIECE_HOVER_HEIGHT, scaleX: 1, scaleY: 1,
        duration: 360, ease: "Elastic.Out", easeParams: [1, 0.55], onComplete: () => this.release() }) });
  }

  land(): void {
    if (!this.motion) { this.release(); return; }
    this.freeze();
    this.scene.tweens.add({ targets: this.body, y: -10, scaleX: 1.12, scaleY: 0.86, duration: 55,
      onComplete: () => this.scene.tweens.add({ targets: this.body, y: -PIECE_HOVER_HEIGHT, scaleX: 1, scaleY: 1,
        duration: 220, ease: "Back.Out", onComplete: () => this.release() }) });
  }

  attack(target: { x: number; y: number }): void {
    if (this.dormant || !this.motion) return;
    this.freeze();
    const dx = target.x - this.root.x;
    const direction = dx >= 0 ? 1 : -1;
    this.scene.tweens.add({ targets: this.body, x: -direction * 5, y: -10, scaleX: 1.14, scaleY: 0.82,
      duration: 75, ease: "Sine.In",
      onComplete: () => this.scene.tweens.add({ targets: this.body, x: direction * 9, y: -24, scaleX: 0.91, scaleY: 1.14,
        duration: 100, ease: "Cubic.Out",
        onComplete: () => this.scene.tweens.add({ targets: this.body, x: 0, y: -PIECE_HOVER_HEIGHT, scaleX: 1, scaleY: 1,
          duration: 220, ease: "Back.Out", onComplete: () => this.release() }) }) });
  }

  hit(direction: number): void {
    if (this.dormant || !this.motion) return;
    this.freeze();
    this.sprite.setTint(0xfff3ce).setTintMode(Phaser.TintModes.FILL);
    this.scene.time.delayedCall(60, () => { if (!this.dormant && this.sprite.active) this.sprite.clearTint().setTintMode(Phaser.TintModes.MULTIPLY); });
    this.scene.tweens.add({ targets: this.body, x: direction * 8, y: -11, angle: direction * 6, scaleX: 1.21, scaleY: 0.78,
      duration: 65, ease: "Cubic.Out",
      onComplete: () => this.scene.tweens.add({ targets: this.body, x: 0, y: -PIECE_HOVER_HEIGHT, angle: 0, scaleX: 1, scaleY: 1,
        duration: 300, ease: "Elastic.Out", easeParams: [1, 0.55], onComplete: () => this.release() }) });
  }

  defeat(): void {
    this.dormant = true; this.freeze(); this.rank.setAlpha(0.4);
    this.setCharges(0, false);
    this.sprite.setTint(0x788c83).setTintMode(Phaser.TintModes.MULTIPLY);
    this.scene.tweens.add({ targets: this.body, y: -3, angle: -7, scaleX: 1.05, scaleY: 0.62, alpha: 0.52,
      duration: this.motion ? 330 : 0, ease: "Bounce.Out" });
    this.scene.tweens.add({ targets: this.aura, alpha: 0, duration: 180 });
  }

  celebrate(delay: number): void {
    if (this.dormant || !this.motion) return;
    this.scene.time.delayedCall(delay, () => {
      this.freeze();
      this.scene.tweens.add({ targets: this.body, y: -52, scaleX: 0.9, scaleY: 1.12, duration: 220, ease: "Cubic.Out",
        onComplete: () => this.scene.tweens.add({ targets: this.body, y: -8, scaleX: 1.2, scaleY: 0.78, duration: 220, ease: "Cubic.In",
          onComplete: () => this.scene.tweens.add({ targets: this.body, y: -PIECE_HOVER_HEIGHT, scaleX: 1, scaleY: 1,
            duration: 220, ease: "Back.Out", onComplete: () => this.release() }) }) });
    });
  }

  destroy(): void {
    this.chargeView?.destroy();
    this.scene.tweens.killTweensOf(this.idleState);
    this.scene.tweens.killTweensOf(this.healthState);
    this.scene.tweens.killTweensOf(this.root);
    this.scene.tweens.killTweensOf(this.body);
    this.scene.tweens.killTweensOf(this.aura);
    this.root.destroy();
  }
}
